import { CartAddEvent, CartErrorEvent } from '@theme/events';

const sectionIds = () =>
  Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => element instanceof HTMLElement ? element.dataset.sectionId : '')
    .filter(Boolean)
    .join(',');

const setButtonState = (button, state) => {
  const label = button.querySelector('.zentrobad-cross-sell__add-label');
  const done = button.querySelector('.zentrobad-cross-sell__add-done');

  button.dataset.state = state;
  button.disabled = state === 'loading';

  if (label instanceof HTMLElement) label.hidden = state === 'added';
  if (done instanceof HTMLElement) done.hidden = state !== 'added';
};

const announce = (button, message) => {
  const container = button.closest('.zentrobad-cross-sell, .zentrobad-cart-cross-sell');
  const status = container?.querySelector('[data-zentrobad-cross-sell-status]');
  if (status instanceof HTMLElement) status.textContent = message;
};

const addCrossSell = async (button) => {
  const variantId = button.dataset.variantId;
  const productTitle = button.dataset.productTitle || 'Produkt';

  if (!variantId || button.dataset.state === 'loading') return;

  setButtonState(button, 'loading');

  const payload = {
    items: [{ id: Number(variantId), quantity: 1 }],
    sections: sectionIds(),
  };

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });
    const result = await response.json();

    if (result.status) {
      throw new Error(result.message || result.description || 'Das Produkt konnte nicht hinzugefügt werden.');
    }

    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();

    document.dispatchEvent(
      new CartAddEvent(cart, variantId, {
        source: 'zentrobad-cross-sell',
        itemCount: cart.item_count,
        variantId,
        productId: button.dataset.productId,
        sections: result.sections,
      })
    );

    setButtonState(button, 'added');
    announce(button, `${productTitle} wurde hinzugefügt.`);

    window.location.assign(Theme.routes.cart_url || '/cart');

    window.setTimeout(() => {
      if (button.dataset.state === 'added') setButtonState(button, 'idle');
    }, 1800);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Das Produkt konnte nicht hinzugefügt werden.';
    document.dispatchEvent(new CartErrorEvent(variantId, message, message, {}));
    setButtonState(button, 'idle');
    announce(button, message);
  }
};

class ZentrobadCartCrossSell extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';
    this.#fillRecommendations();
  }

  async #fillRecommendations() {
    const grid = this.querySelector('[data-zentrobad-cart-cross-sell-grid]');
    const currentCount = Number(this.dataset.currentCount || 0);
    const maxCount = Number(this.dataset.maxCount || 4);
    const needed = Math.max(0, maxCount - currentCount);

    if (!(grid instanceof HTMLElement) || needed === 0) return;

    const endpoint = this.dataset.recommendationsUrl || '/recommendations/products';
    const productIds = [...new Set((this.dataset.productIds || '').split(',').map((id) => id.trim()).filter(Boolean))];
    const excluded = new Set((this.dataset.excludedHandles || '').split('|').filter(Boolean));
    const products = [];

    for (const productId of productIds) {
      if (products.length >= needed) break;

      try {
        const url = new URL(endpoint, window.location.origin);
        url.searchParams.set('product_id', productId);
        url.searchParams.set('limit', '10');
        url.searchParams.set('intent', 'related');
        url.searchParams.set('section_id', 'zentrobad-cart-related-products');

        const response = await fetch(url.toString(), { headers: { Accept: 'text/html' } });
        if (!response.ok) continue;

        const body = new DOMParser().parseFromString(await response.text(), 'text/html');
        for (const card of body.querySelectorAll('.zentrobad-cart-cross-sell__card')) {
          const handle = card.dataset.productHandle;
          if (!handle || excluded.has(handle)) continue;
          excluded.add(handle);
          products.push(card);
          if (products.length >= needed) break;
        }
      } catch (error) {
        console.warn('Zentrobad Produktempfehlungen konnten nicht geladen werden.', error);
      }
    }

    for (const card of products) grid.append(document.importNode(card, true));

    if (!grid.children.length) this.hidden = true;
  }

}

if (!customElements.get('zentrobad-cart-cross-sell')) {
  customElements.define('zentrobad-cart-cross-sell', ZentrobadCartCrossSell);
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const button = target.closest('[data-zentrobad-cross-sell-add]');
  if (!(button instanceof HTMLButtonElement)) return;

  event.preventDefault();
  addCrossSell(button);
});
